import { Link } from "react-router-dom";
import { fmtKm, type Accepted, type PlannerContext, type Proposal } from "../../api/routing";
import type { DayCrews } from "./useDayCrews";

/** Save and accept, pinned to the bottom of the window while the days
 * scroll. Accepting hands the plan to the crews and raises one draft
 * inspection per stop, so it waits until the plan is saved and every day has
 * a crew and a date the server holds. */
export default function PlanBar({
  proposal,
  context,
  crews,
  contractId,
  onContractId,
  planName,
  onPlanName,
  savedId,
  saving,
  onSave,
  accepting,
  accepted,
  onAccept,
}: {
  proposal: Proposal;
  context: PlannerContext;
  crews: DayCrews;
  contractId: string;
  onContractId: (id: string) => void;
  planName: string;
  onPlanName: (name: string) => void;
  savedId: number | null;
  saving: boolean;
  onSave: () => void;
  accepting: boolean;
  accepted: Accepted | null;
  onAccept: () => void;
}) {
  const days = proposal.days.length;
  const sites = proposal.days.reduce((n, d) => n + d.stops.length, 0);
  const metres = proposal.days.reduce((m, d) => m + d.total_m, 0);
  const waiting = days - crews.assignedCount;
  const contracts = context.filters.contracts;
  const acceptBlocker =
    savedId === null ? "Save the plan first" : !crews.allAssigned ? "Every day needs a crew and a date" : accepted ? "Already accepted" : undefined;
  return (
    <div className="sticky-actions plan-bar no-print">
      <strong className="plan-bar-totals">
        {days} day{days === 1 ? "" : "s"} · {sites} site{sites === 1 ? "" : "s"} · {fmtKm(metres)} km
      </strong>
      {accepted ? (
        <span className="plan-bar-crews ok">
          Accepted. {accepted.inspections_created} draft inspection{accepted.inspections_created === 1 ? "" : "s"} raised
          {accepted.inspections_existing ? `, ${accepted.inspections_existing} already existed` : ""}.{" "}
          <Link to={`/routing/plans/${accepted.plan_id}`}>Open the plan</Link>
        </span>
      ) : savedId === null ? (
        <span className="plan-bar-crews">Save the plan, then every day needs its field operators</span>
      ) : (
        <span className={waiting === 0 ? "plan-bar-crews ok" : "plan-bar-crews"}>
          {waiting === 0 ? "Every day has its field operators" : `${waiting} of ${days} day${days === 1 ? "" : "s"} still need${waiting === 1 ? "s" : ""} field operators`}
        </span>
      )}
      <div className="plan-bar-fields">
        {contracts.length === 1 ? null : (
          <select className="select" aria-label="Contract" value={contractId} onChange={(e) => onContractId(e.target.value)} disabled={savedId !== null}>
            <option value="">Contract…</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <input
          className="input"
          aria-label="Plan name"
          placeholder="Plan name (optional)"
          value={planName}
          onChange={(e) => onPlanName(e.target.value)}
          disabled={savedId !== null}
        />
        <button type="button" className="btn btn-secondary" disabled={saving || contractId === "" || savedId !== null} onClick={onSave}>
          {savedId !== null ? `Saved as plan #${savedId}` : saving ? "Saving…" : "Save the plan"}
        </button>
        <button type="button" className="btn btn-primary" disabled={acceptBlocker !== undefined || accepting} title={acceptBlocker} onClick={onAccept}>
          {accepting ? "Accepting…" : "Accept and create inspections"}
        </button>
      </div>
    </div>
  );
}
